import {
  areasOf, coverImage, flattenOnWhite, prepareTemplate, previewFactor, resizeLanczos, shadowMap, toHex, PHOTO_MAX_SIDE,
  type MaskSettings, type MockupJob, type MockupTemplate, type Point, type PrepareOptions, type PreparedMaps,
  type PrintArea, type ResolvedBackdrop, type Rgb, type RgbaImage, type ShadingSettings,
} from '@eip/engine';
import { MOCKUP_PREVIEW_MAX, type PreviewMaps } from './protocol';

/**
 * A decoded template photo made fit for `prepareTemplate`: composited onto white (a cut-out
 * PNG would otherwise seed the background mask from whatever RGB sits under alpha 0) and
 * capped at `PHOTO_MAX_SIDE` on its long side, so the maps a template holds — five arrays the
 * size of the photo — stay bounded however large a file the user drops in.
 *
 * Returns the input object unchanged when it is already opaque and small enough (the shipped
 * built-in JPEGs), so the common path pays one alpha scan and no copy.
 */
export function photoFromBytes(img: RgbaImage): RgbaImage {
  const flat = flattenOnWhite(img);
  const long = Math.max(flat.width, flat.height);
  if (long <= PHOTO_MAX_SIDE) return flat;
  const s = PHOTO_MAX_SIDE / long;
  return resizeLanczos(flat, Math.max(1, Math.round(flat.width * s)), Math.max(1, Math.round(flat.height * s)));
}

/**
 * The garment mask's boundary as a 1-px cyan outline on transparent, for the template editor
 * to lay over the preview. `alpha` is a `PreparedMaps.mask` (0–255 per pixel, photo-sized);
 * `factor` is the preview downscale, and the outline is computed in PREVIEW space — the field
 * is sampled at every `factor`-th source pixel — so the line stays one pixel wide on screen
 * instead of vanishing into a box average. The result is `ceil(w / factor) × ceil(h / factor)`,
 * exactly the size `imageToBitmap(photo, …)` produces at the same factor.
 *
 * A pixel is on the boundary when it is inside the mask (alpha > 128) and at least one of its
 * four neighbours is outside it (off the image counts as outside, so a mask that runs to the
 * edge is still outlined there).
 */
export function maskEdgeRgba(alpha: Uint8Array, w: number, h: number, factor: number): RgbaImage {
  const f = Math.max(1, Math.round(factor));
  const ow = Math.max(1, Math.ceil(w / f)), oh = Math.max(1, Math.ceil(h / f));
  const inside = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= ow || y >= oh) return false;
    return alpha[Math.min(h - 1, y * f) * w + Math.min(w - 1, x * f)] > 128;
  };
  const data = new Uint8ClampedArray(ow * oh * 4);
  for (let y = 0; y < oh; y++) {
    for (let x = 0; x < ow; x++) {
      if (!inside(x, y)) continue;
      if (inside(x - 1, y) && inside(x + 1, y) && inside(x, y - 1) && inside(x, y + 1)) continue;
      const o = (y * ow + x) * 4;
      data[o + 1] = 255; data[o + 2] = 255; data[o + 3] = 255;   // cyan; data[o] stays 0
    }
  }
  return { width: ow, height: oh, data };
}

const samePoint = (a: Point, b: Point): boolean => a[0] === b[0] && a[1] === b[1];
const sameMask = (a: MaskSettings, b: MaskSettings): boolean =>
  a.method === b.method && a.tolerance === b.tolerance && a.bgTolerance === b.bgTolerance && a.featherPx === b.featherPx && a.closePx === b.closePx;
const sameShading = (a: ShadingSettings, b: ShadingSettings): boolean =>
  a.blurPx === b.blurPx && a.foldBlurPx === b.foldBlurPx && a.wrinkle === b.wrinkle && a.texture === b.texture && a.lmid === b.lmid;
/**
 * Only the QUAD, deliberately: `prepareTemplate` seeds the mask flood from the
 * print area's pixels and scopes the gradient quantisation and `lmid` to them,
 * all of which read the quad. The inches are what the quad MEANS at the press —
 * they scale the art, and nothing in the maps depends on them — so a save after
 * nudging Width/Height in the editor must not pay for a second full preparation
 * (~260–2400 ms) that would produce byte-identical maps.
 */
const sameQuad = (a: PrintArea, b: PrintArea): boolean =>
  samePoint(a.quad.tl, b.quad.tl) && samePoint(a.quad.tr, b.quad.tr) && samePoint(a.quad.br, b.quad.br) && samePoint(a.quad.bl, b.quad.bl);

/**
 * Whether a cached template's prepared maps are invalid for `next` — i.e. whether anything
 * `prepareTemplate` reads has changed. Renaming a template, recolouring its swatch label,
 * flipping `recolorable` or resizing the print area in INCHES costs nothing: only the mask
 * settings, the shading settings, the print area's QUAD (it seeds the flood and scopes the
 * gradient quantisation and `lmid`) and the photo's own size force the maps to be rebuilt.
 * `kind` is deliberately not compared: it only picks the DEFAULT mask settings, and a template
 * always carries its resolved ones.
 */
export function needsReprepare(prev: MockupTemplate, next: MockupTemplate): boolean {
  return prev.photo.width !== next.photo.width || prev.photo.height !== next.photo.height
    || !sameMask(prev.mask, next.mask) || !sameShading(prev.shading, next.shading) || !sameAreas(prev, next);
}

/** EVERY area's quad, in order: `prepareMaps` normalises the gradient reference and `lmid` over
 *  the UNION of the areas, so a second area moving — or being added or removed — invalidates the
 *  maps exactly as the primary's quad does. Order-sensitive, because the first area is also the
 *  one the mask flood is seeded from. */
const sameAreas = (a: MockupTemplate, b: MockupTemplate): boolean => {
  const x = areasOf(a), y = areasOf(b);
  return x.length === y.length && x.every((area, i) => sameQuad(area, y[i]));
};

/**
 * The art each of a job's extra prints needs, pulled out of the documents the worker has open —
 * and the areas it could not find any for.
 *
 * Keyed by `MockupPrint.docId`, which is what `renderMockup` looks up — and `'self'` for a print
 * carrying `null`, the key the compositor tries before falling back to `input.art`.
 *
 * The job's OWN document is resolved here too, rather than left to that fallback: `mockupBlank`
 * composites a 1×1 TRANSPARENT primary art (that is what makes it a blank), so a self-printed
 * sleeve left to `input.art` would vanish for the length of every drag — and a print naming its
 * own document by id would be skipped outright, since the compositor has no `input.art` fallback
 * for a non-null docId. Only the PRIMARY print is the one a blank leaves out.
 *
 * A document that is closed, or open with no DTF output yet, is the same thing to a render:
 * there is nothing to print. Its area lands in `missing` (the compositor reports it as `skipped`
 * too) rather than failing the whole render — the other prints still belong on the garment.
 *
 * Pure, and takes only what it reads, so it can be exercised with plain objects.
 */
export function extraArtsFor(
  docs: ReadonlyMap<string, { output?: RgbaImage }>, job: MockupJob, selfId: string,
): { arts: Record<string, RgbaImage>; missing: string[] } {
  const arts: Record<string, RgbaImage> = {};
  const missing: string[] = [];
  for (const p of job.extraPrints ?? []) {
    if (!p || !p.enabled) continue;
    // `null` is this document's art under the compositor's own key; an explicit self-reference is
    // the same art under its document id. Both are the same lookup.
    const docId = p.docId ?? selfId;
    const art = docs.get(docId)?.output;
    if (art) arts[p.docId === null ? 'self' : docId] = art;
    else if (!missing.includes(p.areaId)) missing.push(p.areaId);
  }
  return { arts, missing };
}

/** The key one scene's cover for one photo size is cached under. The `${id}|` prefix is what
 *  `mockupUnloadScene` sweeps by, so it must stay the first thing in the key. */
export const sceneCoverKey = (sceneId: string, w: number, h: number): string => `${sceneId}|${w}x${h}`;

/**
 * One loaded scene scaled to cover `w × h` EXACTLY, memoised in `covers`.
 *
 * Memoised because this is the expensive half of a backdrop: a 3000-px scene resampled to the
 * photo's size is tens of milliseconds, and it is the same pixels for every render of every
 * colourway onto that blank — while the cover itself depends on nothing but the scene and the
 * size, which is the whole of the key.
 *
 * Throws when the worker does not hold that scene: the runner loads a scene before it renders
 * anything that names one, so this is a caller that got ahead of itself (or a scene unloaded
 * under an in-flight render), not something to silently render without.
 */
export function sceneCover(sceneId: string, w: number, h: number, scenes: ReadonlyMap<string, RgbaImage>, covers: Map<string, RgbaImage>): RgbaImage {
  const key = sceneCoverKey(sceneId, w, h);
  const cached = covers.get(key);
  if (cached) return cached;
  const scene = scenes.get(sceneId);
  if (!scene) throw new Error(`scene not loaded: ${sceneId}`);
  const cover = coverImage(scene, w, h);
  covers.set(key, cover);
  return cover;
}

/**
 * A job's `Backdrop` turned into the `ResolvedBackdrop` the compositor takes, or `null` for a
 * render that keeps the photo's own backdrop.
 *
 * `null` in two cases. The job simply has none (`undefined` on anything written before 3C-3,
 * `null` once `sanitizeJob` has seen it). Or the template is NOT separable: without a trustworthy
 * silhouette there is no telling backdrop from garment, so `prepareTemplate` produced no shadow
 * map and `renderMockup` would ignore the backdrop anyway — resolving one would cost a full-photo
 * resample per render for pixels nothing reads. Flipping the job back onto a separable blank
 * brings the swap back, because nothing about the job was changed here.
 *
 * Colour and gradient are returned as FRESH objects: the result crosses into the compositor, and
 * handing it the job's own arrays would let a later edit of that job (or a structured-clone quirk)
 * reach pixels already being rendered. A scene is the cached cover — `covers` is the worker's own
 * cache, and this is the only thing that writes it besides `mockupSceneCover`.
 *
 * Pure apart from that cache, and takes only what it reads, so it can be exercised with plain maps.
 */
export function resolveBackdrop(
  job: MockupJob, separable: boolean, W: number, H: number,
  scenes: ReadonlyMap<string, RgbaImage>, covers: Map<string, RgbaImage>,
): ResolvedBackdrop | null {
  const b = job.backdrop;
  if (!b || !separable) return null;
  if (b.kind === 'color') return { kind: 'color', rgb: [b.rgb[0], b.rgb[1], b.rgb[2]] };
  if (b.kind === 'gradient') return { kind: 'gradient', top: [b.top[0], b.top[1], b.top[2]], bottom: [b.bottom[0], b.bottom[1], b.bottom[2]] };
  return { kind: 'scene', image: sceneCover(b.sceneId, W, H, scenes, covers) };
}

/** A colourway sheet tile's caption: the blank's own colour name when the job prints it
 *  unrecoloured, else the recolour as an upper-case hex (the sheet font is upper-case only). */
export function sheetLabel(job: MockupJob, template: MockupTemplate): string {
  return job.garment ? toHex(job.garment).toUpperCase() : template.colorName;
}

/** `prepareTemplate`'s inputs for a template that already carries its settings. The seed is left
 *  out on purpose: the engine falls back to `centreOf(printAreas[0])`, which is the point the maps
 *  behind any stored template were flooded from.
 *
 *  EVERY area, not just the primary: the gradient reference and `lmid` are normalised over their
 *  union, so a blank prepared from area 0 alone would light the ink in a sleeve against the
 *  chest's exposure. */
export const prepareOptionsFor = (t: MockupTemplate): PrepareOptions => ({ kind: t.kind, mask: t.mask, shading: t.shading, printAreas: areasOf(t) });

/**
 * One setting expressed in FULL-RES photo pixels, brought to preview scale.
 *
 * `min` is the smallest value the engine's own validation accepts for the field (`parseTemplate`,
 * `model.ts`): 0 where 0 is a legal "off" (`featherPx` = a hard edge, `closePx` = no closing), 1
 * where a numeric value is always a real radius (`blurPx` 1–30, `foldBlurPx` 1–60). A value already
 * at or below `min` is left exactly where it is; anything above it rounds to the nearest whole pixel
 * and never falls below 1 — a radius that scales to "0.4 px" is still a blur, and rounding it away
 * would make the preview SHARPER than the export rather than merely coarser.
 */
const scalePx = (v: number, factor: number, min: 0 | 1): number => {
  if (!Number.isFinite(v) || v <= min) return v;
  const floor = v >= 1 ? 1 : min;                   // a sub-pixel feather may scale to a hard edge
  return Math.max(floor, Math.round(v / factor));
};

/**
 * The same preparation, described for a photo `factor` times smaller.
 *
 * Four of a template's settings are measured in the photo's OWN pixels — `mask.featherPx`,
 * `mask.closePx`, and `shading.blurPx`/`shading.foldBlurPx` when they are numbers rather than
 * `'auto'` — so handing them to `prepareTemplate` on a downscaled photo would make every one of them
 * `factor` times too wide relative to the image: a template whose blur is set by hand would get
 * preview maps blurrier than the full-res maps the export uses, which is a WYSIWYG gap and not a
 * rounding difference. `'auto'` values are left alone because they already derive from the width
 * (`autoBlurPx`/`autoFoldBlurPx` in `shading.ts`), and everything else is untouched: `tolerance` and
 * `bgTolerance` are colour distances, `wrinkle` is a fraction of the width, and `texture`/`lmid` are
 * unitless.
 *
 * `printArea`/`printAreas`/`seed` are deliberately NOT scaled — quads are the caller's business
 * (`previewMapsFor` scales them with `scaleAreas`, which keeps the inches a quad MEANS unchanged),
 * and with areas supplied the engine derives the seed from them.
 *
 * Pure, and factor 1 is not a special case: dividing by 1 and rounding an integer returns it.
 */
export function scalePrepareOptions(opts: PrepareOptions, factor: number): PrepareOptions {
  if (!(factor > 1)) return opts;
  const out: PrepareOptions = { ...opts };
  const m = opts.mask;
  if (m) {
    out.mask = { ...m };
    if (typeof m.featherPx === 'number') out.mask.featherPx = scalePx(m.featherPx, factor, 0);
    if (typeof m.closePx === 'number') out.mask.closePx = scalePx(m.closePx, factor, 0);
  }
  const s = opts.shading;
  if (s) {
    out.shading = { ...s };
    if (typeof s.blurPx === 'number') out.shading.blurPx = scalePx(s.blurPx, factor, 1);
    if (typeof s.foldBlurPx === 'number') out.shading.foldBlurPx = scalePx(s.foldBlurPx, factor, 1);
  }
  return out;
}

const round01 = (v: number): number => Math.round(v * 100) / 100;

/**
 * A template's print-area quads at PREVIEW scale: every coordinate divided by `factor` and rounded
 * to 0.01 px (the homography math on a downscaled photo has no use for sub-hundredth precision).
 * Inches, ids and names ride along unchanged — a factor never touches what a quad MEANS at the
 * press, only where it sits on this particular bitmap. Factor 1 is not a special case: dividing by
 * 1 and rounding to 0.01 already returns the same coordinates.
 */
export function scaleAreas(areas: PrintArea[], factor: number): PrintArea[] {
  const pt = (p: Point): Point => [round01(p[0] / factor), round01(p[1] / factor)];
  return areas.map((a) => ({ id: a.id, name: a.name, widthIn: a.widthIn, heightIn: a.heightIn, quad: { tl: pt(a.quad.tl), tr: pt(a.quad.tr), br: pt(a.quad.br), bl: pt(a.quad.bl) } }));
}

/** What `mockupPreviewMaps` needs from a prepared template: `engine.worker.ts`'s own `TemplateEntry`
 *  satisfies this structurally, and a test can hand over a smaller synthetic one. */
export interface PreviewMapsEntry { template: MockupTemplate; photo: RgbaImage; maps: PreparedMaps; prepGeneration: number }

/**
 * `mockupPreviewMaps`'s pure body: everything but turning the (possibly downscaled) photo into an
 * `ImageBitmap`, which needs the browser's `createImageBitmap` and so cannot run in a vitest test —
 * the caller does that with the worker's own bitmap helper and folds it into the `photo` field.
 *
 * Preview dimensions are exactly what `imageToBitmap(photo, Math.min(maxDim, MOCKUP_PREVIEW_MAX))`
 * would produce for the SAME photo — the CPU preview bitmap the viewer already gets from
 * `mockupRender` — so the two line up pixel-for-pixel.
 *
 * At factor 1 (true for the built-in templates against `MOCKUP_PREVIEW_MAX`) the maps are COPIES of
 * the entry's own full-res arrays: no resize, no second `prepareTemplate`. `.slice()` matters here —
 * these arrays are about to be `Comlink.transfer`red, which detaches the buffer it moves, and the
 * entry's own maps must survive that. Past factor 1 the photo is downscaled with `resizeLanczos`,
 * the areas scaled to match (`scaleAreas`) and every setting measured in photo pixels scaled with
 * them (`scalePrepareOptions`), then the whole template is prepared again at that size — freshly
 * allocated arrays that are safe to transfer as they are.
 *
 * `garmentColor` is always the TEMPLATE's own field, never re-measured at preview scale:
 * `renderMockup` recolours against `template.garmentColor`, and the preview must agree with it.
 */
export function previewMapsFor(entry: PreviewMapsEntry, maxDim: number): Omit<PreviewMaps, 'photo'> & { photo: RgbaImage } {
  const { template, photo, maps, prepGeneration } = entry;
  const factor = previewFactor(photo.width, photo.height, Math.min(maxDim, MOCKUP_PREVIEW_MAX));
  const garmentColor: Rgb = template.garmentColor;
  const recolorable = template.recolorable !== false;
  if (factor === 1) {
    return {
      templateId: template.id, prepGeneration, width: photo.width, height: photo.height, factor,
      photo,
      mask: maps.mask.slice(), lumaBlur: maps.lumaBlur.slice(), gx: maps.gx.slice(), gy: maps.gy.slice(), residual: maps.residual.slice(), lmid: maps.lmid,
      // `shadow` rides along under exactly the same rule as the other five arrays — a COPY, because
      // the entry's own maps must survive the transfer. Absent for a non-separable template, which
      // is what tells the GPU preview to ignore the job's backdrop exactly as `renderMockup` does.
      shadow: maps.shadow?.slice(),
      areas: areasOf(template), garmentColor, recolorable,
    };
  }
  const width = Math.ceil(photo.width / factor), height = Math.ceil(photo.height / factor);
  const previewPhoto = resizeLanczos(photo, width, height);
  const scaledAreas = scaleAreas(areasOf(template), factor);
  // `scalePrepareOptions`: the areas are not the only thing measured in this photo's pixels — the
  // mask's feather/close and a manual shading blur are too, and left unscaled they would be `factor`
  // times too wide on a photo `factor` times smaller (see the helper).
  const prep = prepareTemplate(previewPhoto, scalePrepareOptions({ ...prepareOptionsFor(template), printAreas: scaledAreas }, factor));
  return {
    templateId: template.id, prepGeneration, width, height, factor,
    photo: previewPhoto,
    mask: prep.maps.mask, lumaBlur: prep.maps.lumaBlur, gx: prep.maps.gx, gy: prep.maps.gy, residual: prep.maps.residual, lmid: prep.maps.lmid,
    // Freshly allocated by the preparation above, so it transfers as it is — and measured at THIS
    // size, which is what a preview-scale shadow has to be.
    //
    // Gated on the ENTRY's own verdict, not the re-preparation's: `separable` is measured on the
    // photo it is given, and a borderline user blank past factor 1 can come out separable at preview
    // size while the full-res preparation the CPU, the ticket and the panel all read did not (or the
    // reverse). Presence of this one map is the whole backdrop predicate, so the two renderers have
    // to answer it from the same measurement.
    shadow: maps.shadow ? prep.maps.shadow ?? shadowMap(prep.maps.mask, width, height) : undefined,
    areas: prep.printAreas, garmentColor, recolorable,
  };
}
